import { Breadcrumbs } from "components/breadcrumbs"
import { Filter, OrderCard, OrderTable } from "./components"
import { useOne, useTable } from "@refinedev/core"
import { useEffect, useMemo, useState } from "react"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import { IOrderItem } from "interfaces"
import Button from "components/button"
import { ChevronDownIcon, DownloadIcon } from "@heroicons/react/outline"
import { get } from "lodash"
import { DropdownMenu } from "components/dropdownMenu"
import { isHaveActionPermission } from "utills/helpers"

export const OrderList: React.FC = () => {
  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IOrderItem>({
    resource: "checkout/orders",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "orders",
    },
  });

  const [exportId, setExportId] = useState<string | null>();

  const { refetch: checkStatus } = useOne({
    resource: "export/result",
    id: exportId ?? "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false,
    } as any,
    errorNotification: (data, values, resource) => {
      setExportId(null);
      return {
        message: `Export Error`,
        description: "Export Error",
        type: "error",
      }
    },
  });

  const { refetch: getOrderExportData, isFetching: isFetchingOrderExportData } = useOne<any>({
    resource: "export/order-list",
    id: "",
    meta: {
      resource: "data",
      filters: [...filters].concat([{field: "currency", operator: "eq", value: (localStorage.getItem("currency") ?? "vnd").toLowerCase()}])
    },
    queryOptions: {
      enabled: false, // Disable automatic fetching
    } as any,
  });

  const { refetch: getUsedCodesExportData } = useOne<any>({
    resource: "export/order-used-code",
    id: "",
    meta: {
      resource: "data",
      filters: [...filters].concat([{field: "currency", operator: "eq", value: (localStorage.getItem("currency") ?? "vnd").toLowerCase()}])
    },
    queryOptions: {
      enabled: false, // Disable automatic fetching
    } as any,
  });

  const { refetch: getReconciliationExportData } = useOne<any>({
    resource: "export/order-reconciliation",
    id: "",
    meta: {
      resource: "data",
      filters: [...filters].concat([{field: "currency", operator: "eq", value: (localStorage.getItem("currency") ?? "vnd").toLowerCase()}])
    },
    queryOptions: {
      enabled: false, // Disable automatic fetching
    } as any,
  });

  const { refetch: getOrderItemExportData } = useOne<any>({
    resource: "export/order-items",
    id: "",
    meta: {
      resource: "data",
      filters: [...filters].concat([{field: "currency", operator: "eq", value: (localStorage.getItem("currency") ?? "vnd").toLowerCase()}])
    },
    queryOptions: {
      enabled: false, // Disable automatic fetching
    } as any,
  });

  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        checkStatus().then((data) => {
          const isSuccess = get(data, "data.data.is_success", false);
          if (isSuccess) {
            setExportId(null);
            window.location.href = get(data, "data.data.file_link", "#");
          }
        });
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [exportId, checkStatus]);

  const _checkStatus = (id: string) => {
    setExportId(id);
  }

  const _exportOrder = () => {
    getOrderExportData().then(res => {
      console.log(res);
      if (res.isSuccess) {
        const exportId = get(res, "data.data.export_id", "");
        _checkStatus(exportId)
      }
    });
  }

  const _exportUsedCodes = () => {
    getUsedCodesExportData().then(res => {
      if (res.isSuccess) {
        const exportId = get(res, "data.data.export_id", "");
        _checkStatus(exportId)
      }
    });
  }

  const _exportOrderItems = () => {
    getOrderItemExportData().then(res => {
      if (res.isSuccess) {
        const exportId = get(res, "data.data.export_id", "");
        _checkStatus(exportId)
      }
    });
  }

  const _exportReconciliations = () => {
    getReconciliationExportData().then(res => {
      if (res.isSuccess) {
        const exportId = get(res, "data.data.export_id", "");
        _checkStatus(exportId)
      }
    });
  }

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value || "",
      order_type: logicalFilters.find((item) => item.field === "order_type")?.value || "",
      payment_method: logicalFilters.find((item) => item.field === "payment_method")?.value || "",
      order_status: logicalFilters.find((item) => item.field === "order_status")?.value || "",
      is_vat: logicalFilters.find((item) => item.field === "is_vat")?.value || "",
      is_private_flow: logicalFilters.find((item) => item.field === "is_private_flow")?.value || "",
      sc_from: logicalFilters.find((item) => item.field === "sc_from")?.value || "",
      sc_to: logicalFilters.find((item) => item.field === "sc_to")?.value || "",
      lte: logicalFilters.find((item) => item.field === "lte")?.value || "",
      gte: logicalFilters.find((item) => item.field === "gte")?.value || "",
      ticket_platform: logicalFilters.find((item) => item.field === "ticket_platform")?.value || "",
      reference_number: logicalFilters.find((item) => item.field === "reference_number")?.value || "",
    };
  }, [filters]);

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission("", "orders", "export")) && {
          additionalLabel: (
            <div className="flex items-center gap-x-3">
              <DropdownMenu
                position="bottom"
                label={<div className="button w-fit bg-white rounded-md text-brand-500 font-bold border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85">
                  More actions
                  <ChevronDownIcon className="ml-2 size-4 text-brand-500" />
                </div>}
                items={[
                  {
                    label: "Export Order Item",
                    icon: <DownloadIcon className="size-4" />,
                    onClick: () => _exportOrderItems(),
                  },
                  {
                    label: "Export Used Code",
                    icon: <DownloadIcon className="size-4" />,
                    onClick: () => _exportUsedCodes(),
                  },
                  {
                    label: "Export Reconciliation",
                    icon: <DownloadIcon className="size-4" />,
                    onClick: () => _exportReconciliations(),
                  },
                ]} />

              <Button
                onClick={_exportOrder}
                loading={isFetchingOrderExportData}
                className="w-11 h-11 sm:w-auto"
              >
                <span className="sr-only">Export order</span>
                <DownloadIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Export order</span>
              </Button>
            </div>
          )
        }}      
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <OrderCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
              />
            </div>
            <div className="hidden sm:block">
              <OrderTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_item ?? 0,
                total: tableQuery?.data?.paging.total
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQuery.isLoading || (tableQuery.isFetching && tableQuery.isPreviousData)) || exportId ? <FullScreenLoading /> : <></>}
    </div>
  )
}